import React, { createContext, useContext, useState, useEffect } from 'react'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(false) // No loading initially

  // Mock authentication for now
  useEffect(() => {
    // Check for stored user in localStorage
    const storedUser = localStorage.getItem('tarotPairs_user')
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser))
      } catch (error) {
        console.error('Error parsing stored user:', error)
        localStorage.removeItem('tarotPairs_user')
      }
    }
  }, [])

  const login = async (email, password) => {
    try {
      setLoading(true)
      
      // Mock login - accept any credentials for demo
      const mockUser = {
        id: 'demo-user-' + Date.now(),
        name: email.split('@')[0] || 'Demo User',
        email: email,
        role: email.includes('admin') ? 'admin' : 'user',
        isPremium: email.includes('premium') || email.includes('admin'),
        status: 'active',
        joinDate: new Date().toISOString(),
        lastActive: new Date().toISOString()
      }
      
      setUser(mockUser)
      localStorage.setItem('tarotPairs_user', JSON.stringify(mockUser))
      
      return mockUser
    } catch (error) {
      throw new Error(error.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const register = async (email, password, name) => {
    try {
      setLoading(true)
      
      // Mock registration
      const mockUser = {
        id: 'user-' + Date.now(),
        name: name || email.split('@')[0],
        email: email,
        role: 'user',
        isPremium: false,
        status: 'active',
        joinDate: new Date().toISOString(),
        lastActive: new Date().toISOString()
      }
      
      setUser(mockUser)
      localStorage.setItem('tarotPairs_user', JSON.stringify(mockUser))
      
      return mockUser
    } catch (error) {
      throw new Error(error.message || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      setUser(null)
      localStorage.removeItem('tarotPairs_user')
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  const upgradeToPremium = async () => {
    if (!user) return
    
    try {
      const updatedUser = {
        ...user,
        isPremium: true,
        role: user.role === 'user' ? 'premium' : user.role
      }
      
      setUser(updatedUser)
      localStorage.setItem('tarotPairs_user', JSON.stringify(updatedUser))
    } catch (error) {
      console.error('Error upgrading to premium:', error)
    }
  }

  const value = {
    user,
    login,
    register,
    logout,
    upgradeToPremium,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}